/**
 * CorrectionIssuesBanner — per-step banner listing the FIELD corrections the
 * backoffice asked on the CURRENT wizard step (PR-ONB-PRECHECK-3).
 *
 * Renders nothing when the session carries no issues (anonymous flow, plain
 * DRAFT) or when none of the open issues belong to this step. The server is
 * the enforcement point (autosave rejects out-of-whitelist edits with
 * ONBOARDING_FIELD_NOT_REOPENED) — this banner is guidance, not a gate.
 */

import { useLocation } from 'react-router-dom';
import { Banner } from '@ab-platform/ui/banner';
import { useWizardIssues } from '@/hooks/WizardIssuesContext';
import { WIZARD_STEP_ORDER, type WizardStepKey } from '@/lib/accountBindFlow';
import {
  issueReasonLabel,
  issueTargetLabel,
  issuesForStep,
} from '@/lib/correctionIssuesView';

function stepFromPathname(pathname: string): WizardStepKey | null {
  const last = pathname.split('/').filter(Boolean).pop() ?? '';
  return (WIZARD_STEP_ORDER as readonly string[]).includes(last)
    ? (last as WizardStepKey)
    : null;
}

export function CorrectionIssuesBanner(): JSX.Element | null {
  const issues = useWizardIssues();
  const location = useLocation();
  const step = stepFromPathname(location.pathname);
  if (!step || issues.length === 0) return null;

  const stepIssues = issuesForStep(step, issues);
  if (stepIssues.length === 0) return null;

  return (
    <div className="mx-auto mt-3 w-full max-w-2xl px-4">
      <Banner tone="warning" title="Correzioni richieste su questo passaggio">
        <ul className="grid list-disc gap-1 pl-5">
          {stepIssues.map((issue) => (
            <li key={issue.id} className="text-sm">
              <strong>{issueTargetLabel(issue.targetKey)}</strong> —{' '}
              {issueReasonLabel(issue.reasonCode)}
              {issue.note ? <>: {issue.note}</> : null}
            </li>
          ))}
        </ul>
      </Banner>
    </div>
  );
}
